import { Navigate, Outlet } from "react-router-dom";

import { useFeatureAccess, useFlagsReady } from "@/shared/feature-flags";
import { CAPABILITIES, type Capability } from "@ibada/shared-types/feature-flags";

/**
 * Route guard for feature-flagged modules. Renders the nested routes only when the flag `capability` is
 * available; otherwise redirects up to the branch dashboard. Pairs with the nav-level hiding so a disabled
 * module is both hidden AND deep-link safe (typing /reports when reports-beta is off won't reach it). This
 * is a UX gate; the API enforces any sensitive action independently.
 */
export function RequireCapability({ capability }: { capability: Capability }) {
  const ready = useFlagsReady();
  const { allowed } = useFeatureAccess(capability);
  // Wait for flags before deciding, so a hard refresh / deep link onto a flagged page doesn't redirect on
  // the fail-safe default during the brief initial flag load.
  if (CAPABILITIES[capability].flag !== null && !ready) return null;
  if (!allowed) return <Navigate to=".." replace />;
  return <Outlet />;
}
